import { FC, useContext, useEffect, useMemo, useState } from "react";

import { Pagination, useMediaQuery } from "@mui/material";

import { toast } from "react-toastify";
import { AddIcon } from "../../../assets/img/icons";
import Button from "../../../components/Button";
import Input from "../../../components/Input";
import Loader from "../../../components/Loader";
import { Select } from "../../../components/OldSelect";
import { AuthContext } from "../../../contexts/AuthContext";
import { addTag, getAllTags } from "../../../services/api/admin/tags";
import { Tag as TagType } from "../../../types/api/admin/tag";
import {
    FieldErrors,
    REQUIRED_FIELD,
    clearFieldError,
    notifyFieldErrors,
} from "../../../utils/errors";
import EditTagModal from "./EditTagModal";
import styles from "./styles.module.scss";
import Tag from "./Tag";

type Props = {
    handleTabChange: (key: string) => void;
};

const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

const Tags: FC<Props> = ({ handleTabChange }) => {
    const isMobile = useMediaQuery("(max-width: 1050px)");
    const { hasPermission } = useContext(AuthContext)!;
    const canReadSettings = hasPermission("admin-settings", "read");

    const [newTagName, setNewTagName] = useState("");
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [editingTag, setEditingTag] = useState<Partial<TagType> | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [tags, setTags] = useState<TagType[]>();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState<number>(10);

    useEffect(() => {
        getAllTags().then((response) => {
            setTags(response);
            setIsLoading(false);
        });
    }, []);

    const maxPage = useMemo(() => {
        const total = tags?.length ?? 0;
        return Math.max(1, Math.ceil(total / pageSize));
    }, [tags, pageSize]);

    useEffect(() => {
        if (page > maxPage) {
            setPage(maxPage);
        }
    }, [page, maxPage]);

    const paginatedTags = useMemo(() => {
        if (!tags) return [];
        const start = (page - 1) * pageSize;
        return tags.slice(start, start + pageSize);
    }, [tags, page, pageSize]);

    const handleAddTag = () => {
        if (newTagName.trim() === "") {
            const nameErrors = { name: REQUIRED_FIELD };
            setFieldErrors(nameErrors);
            notifyFieldErrors(nameErrors, {
                message: "Le nom du tag ne peut pas être vide.",
                type: "warning",
            });
            return;
        }
        setFieldErrors({});

        addTag({ name: newTagName })
            .then((newTag) => {
                if (newTag) {
                    setTags((prevTags) =>
                        prevTags ? [newTag, ...prevTags] : [newTag]
                    );
                    setPage(1);
                }

                toast.success("Tag ajouté avec succès !");
                setNewTagName("");
            })
            .catch(() => {
                return;
            });
    };

    const handleEditTag = (id: string, newName: string) => {
        setEditingTag({ id, name: newName });
    };

    const handleDeleteTag = (id: string) => {
        setTags((prevTags) => prevTags?.filter((tag) => tag.id !== id));
    };

    return (
        <div className={styles.form}>
            <EditTagModal
                isOpen={!!editingTag}
                tag={editingTag || {}}
                onSubmit={() => {
                    setEditingTag(null);
                    getAllTags().then((response) => {
                        setTags(response);
                    });
                }}
                onClose={() => setEditingTag(null)}
            />
            <div className={styles.header}>
                {isMobile ? (
                    <Select
                        value="tags"
                        className={styles.selectTab}
                        onChanged={(value) =>
                            handleTabChange(
                                value === "tags" ? "" : (value as string)
                            )
                        }
                    >
                        <option value="tags">Tags</option>
                        {canReadSettings && (
                            <option value="progression">Progression</option>
                        )}
                        {canReadSettings && (
                            <option value="journal">Journal</option>
                        )}
                    </Select>
                ) : (
                    <span className={styles.form_header}>Tags</span>
                )}
            </div>
            <div className={styles.sections}>
                {isLoading ? (
                    <Loader />
                ) : (
                    <div className={styles.section}>
                        <div className={styles.tag_form}>
                            <Input
                                placeholder="Nom du tag"
                                className={styles.filter_input}
                                value={newTagName}
                                onChanged={(e) => {
                                    setNewTagName(e);
                                    setFieldErrors((prev) =>
                                        clearFieldError(prev, "name")
                                    );
                                }}
                                error={fieldErrors.name}
                            />
                            <Button
                                Icon={AddIcon}
                                size="medium"
                                onClick={handleAddTag}
                            >
                                {!isMobile && "Ajouter"}
                            </Button>
                        </div>
                        {paginatedTags.map((tag) => (
                            <Tag
                                key={tag.id}
                                id={tag.id}
                                name={tag.name}
                                onEdit={() => handleEditTag(tag.id, tag.name)}
                                onDelete={() => handleDeleteTag(tag.id)}
                            />
                        ))}
                        {(tags?.length ?? 0) > 0 && (
                            <div className={styles.paginationRow}>
                                <Pagination
                                    count={maxPage}
                                    page={page}
                                    onChange={(_, value) => setPage(value)}
                                    siblingCount={0}
                                />
                                <Select
                                    value={String(pageSize)}
                                    onChanged={(value) => {
                                        setPageSize(Number(value));
                                        setPage(1);
                                    }}
                                    className={styles.paginationSelect}
                                >
                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={String(size)}>
                                            {size}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Tags;
